// ============================================================================
// UploadPhoto — drag-drop / click-to-upload image control.
// ============================================================================
// Wraps the pre-signed PUT URL flow added in Phase 3d-1f. Backend hands back
// { upload_url, public_url }; the browser PUTs the file directly to S3 with
// progress tracked via XHR (fetch doesn't expose upload progress in any
// browser without a polyfill). On success, onChange(public_url) is called.
//
// Manual paste / removal is still possible: the text input is editable and
// the "Remove" button clears it. So this control augments the URL field
// rather than replacing it — admins who already host elsewhere can paste in.
// ============================================================================

import { useRef, useState } from 'react';
import { Upload, X, Loader2 } from 'lucide-react';
import { api } from '../api';

interface Props {
  token: string;
  value: string;
  onChange: (next: string) => void;
  destination: 'presenters' | 'events' | 'locations';
  disabled?: boolean;
  // Optional accept attribute override. Default covers common image types.
  accept?: string;
  // Visual style for the preview area — circle for headshots, banner for hero.
  shape?: 'circle' | 'banner';
}

const DEFAULT_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';
const MAX_BYTES = 5 * 1024 * 1024;  // 5 MB

function uploadFileWithProgress(
  url: string,
  file: File,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${xhr.status}): ${xhr.responseText || xhr.statusText}`));
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(file);
  });
}

export function UploadPhoto({
  token,
  value,
  onChange,
  destination,
  disabled,
  accept = DEFAULT_ACCEPT,
  shape = 'circle',
}: Props) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string>('');
  const [dragOver, setDragOver] = useState(false);

  const startUpload = async (file: File) => {
    setError('');

    if (!accept.split(',').includes(file.type)) {
      setError(`Unsupported file type: ${file.type || 'unknown'}. JPG / PNG / WebP / GIF only.`);
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(`File is ${(file.size / 1024 / 1024).toFixed(1)} MB. Max is 5 MB.`);
      return;
    }

    setUploading(true);
    setProgress(0);
    try {
      const { upload_url, public_url } = await api.signUpload(token, {
        filename: file.name,
        content_type: file.type,
        destination,
      });
      await uploadFileWithProgress(upload_url, file, setProgress);
      onChange(public_url);
    } catch (err: any) {
      setError(err?.message || 'Upload failed.');
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void startUpload(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled || uploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) void startUpload(file);
  };

  const handleRemove = () => {
    onChange('');
    setError('');
  };

  const previewClass =
    shape === 'banner'
      ? 'w-full aspect-[21/9] object-cover rounded-md'
      : 'h-20 w-20 rounded-full object-cover ring-2 ring-uif-green/20';

  return (
    <div className="space-y-2">
      {value && (
        <div className="flex items-center gap-3">
          <img
            src={value}
            alt="Current upload"
            className={previewClass}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <button
            type="button"
            onClick={handleRemove}
            disabled={disabled || uploading}
            className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <X className="h-3 w-3" />
            Remove
          </button>
        </div>
      )}

      <div
        onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !disabled && !uploading && fileRef.current?.click()}
        className={
          'flex items-center justify-center gap-2 border-2 border-dashed rounded-md px-3 py-3 text-sm cursor-pointer transition-colors ' +
          (disabled || uploading
            ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
            : dragOver
              ? 'border-uif-green bg-uif-green/5 text-uif-green-dark'
              : 'border-gray-300 bg-white hover:border-uif-green/50 text-gray-600')
        }
      >
        {uploading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Uploading… {progress}%</span>
          </>
        ) : (
          <>
            <Upload className="h-4 w-4" />
            <span>{value ? 'Replace photo' : 'Click to upload or drop an image here'}</span>
          </>
        )}
        <input
          ref={fileRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          disabled={disabled || uploading}
          className="hidden"
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded px-2 py-1 text-xs">
          {error}
        </div>
      )}

      <p className="text-[11px] text-gray-500">
        JPG / PNG / WebP / GIF up to 5 MB. The uploaded file is served via the events.upinfarms.com
        CDN — share the resulting URL anywhere.
      </p>
    </div>
  );
}
